import { debounce } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "../../../../../app/hooks";
import { fetchTalentsAutocomplete } from "../../../../../features/talents/talentsSlice";
import { TalentStatus } from "../../../../../features/talents/types/common";
import { TALENTS_PAGE_SIZE } from "../../pagination/constants";
import {
  mapTalentToOption,
  TalentAutocompleteOption,
} from "./talentAutocompleteUtils";

const useTalentAutocomplete = (status?: TalentStatus) => {
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [talentOptions, setTalentOptions] = useState<
    TalentAutocompleteOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const latestRequestIdRef = useRef(0);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((term: string) => {
        setSearchTerm(term);
      }, 400),
    [],
  );

  const resetSearch = useMemo(
    () => () => {
      debouncedSetSearchTerm.clear();
      latestRequestIdRef.current += 1;
      setSearchTerm("");
      setTalentOptions([]);
      setIsLoading(false);
    },
    [debouncedSetSearchTerm],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.clear();
    };
  }, [debouncedSetSearchTerm]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setTalentOptions([]);
      setIsLoading(false);
      return;
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    setIsLoading(true);

    dispatch(
      fetchTalentsAutocomplete({
        search: searchTerm,
        status,
        // NOTE: page size 500 might leave some results out of the response
        // when searching with too few characters
        pageSize: TALENTS_PAGE_SIZE,
      }),
    )
      .then((action) => {
        if (latestRequestIdRef.current !== requestId) return;
        if (isFulfilled(action)) {
          setTalentOptions(action.payload.data.map(mapTalentToOption));
        }
      })
      .finally(() => {
        if (latestRequestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });
  }, [dispatch, searchTerm, status]);

  return {
    talentOptions,
    isLoading,
    setSearchTerm: debouncedSetSearchTerm,
    resetSearch,
  };
};

export default useTalentAutocomplete;
